import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { drugAPI } from '../services/api';
import recentSearchesManager from '../utils/recentSearches';
import RecentSearchesSidebar from '../components/RecentSearchesSidebar';
import SEO from '../components/SEO';

function DrugSearch() {
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: 'FDA Drug Search',
    description: 'Search FDA-approved drugs with detailed information',
    mainEntity: {
      '@type': 'MedicalEntity',
      name: 'Drug Database'
    }
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchType, setSearchType] = useState('search'); // search, adverse, labels
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const resultsPerPage = 10;

  // Auto-search if query parameter is present
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && query.trim()) {
      setSearchQuery(query);
      performSearch(query, searchType, 1);
    }
  }, [searchParams, searchType]);

  const performSearch = async (query, type, page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const skip = (page - 1) * resultsPerPage;
      let response;

      switch (type) {
        case 'search':
          response = await drugAPI.search(query, resultsPerPage, skip);
          break;
        case 'adverse':
          response = await drugAPI.getAdverseEvents(query, resultsPerPage, skip);
          break;
        case 'labels':
          response = await drugAPI.getLabels(query, resultsPerPage, skip);
          break;
        default:
          response = await drugAPI.search(query, resultsPerPage, skip);
      }

      setResults(response.data);
      setCurrentPage(page);

      // Calculate total pages from meta data
      if (response.data.meta?.results?.total) {
        const total = response.data.meta.results.total;
        setTotalPages(Math.ceil(total / resultsPerPage));
      } else {
        setTotalPages(1);
      }

      // Save to recent searches (only on first page)
      if (page === 1) {
        recentSearchesManager.addSearch({
          query: query.trim(),
          database: 'drugs',
          searchType: type,
          resultsCount: response.data?.results?.length || 0
        });
      }

    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // Update URL with search query
    setSearchParams({ q: searchQuery.trim() });

    await performSearch(searchQuery.trim(), searchType, 1);
  };

  const handlePageChange = async (page) => {
    if (page < 1 || page > totalPages) return;
    await performSearch(searchQuery.trim(), searchType, page);
  };  const renderResults = () => {
    if (!results) return null;

    // Check for API error
    if (results.error) {
      return (
        <div style={{ marginTop: '2rem' }}>
          <p style={{ color: '#e74c3c' }}>⚠️ {results.error}</p>
        </div>
      );
    }

    if (searchType === 'search' && results.results) {
      if (results.results.length === 0) {
        return <p className="no-results">No drugs found for "{searchQuery}"</p>;
      }
      
      return (
        <div className="results-grid">
          {results.results.map((drug, index) => {
            const applicationNumber = drug.application_number || `drug-${index}`;
            return (
            <Link 
              key={index} 
              to={`/drug/${applicationNumber}`}
              className="result-card clickable-card"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>{drug.openfda?.brand_name?.[0] || 'Unknown Brand'}</h3>
                <span className="badge badge-info" style={{ fontSize: '0.75rem', flexShrink: 0, marginLeft: '0.5rem' }}>FDA</span>
              </div>
              
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Basic Information</h4>
                <p><strong>Generic Name:</strong> {drug.openfda?.generic_name?.[0] || 'N/A'}</p>
                <p><strong>Manufacturer:</strong> {drug.openfda?.manufacturer_name?.[0] || 'N/A'}</p>
                <p><strong>Sponsor:</strong> {drug.sponsor_name || 'N/A'}</p>
                <p><strong>Application Number:</strong> {drug.application_number || 'N/A'}</p>
                {drug.openfda?.product_type && (
                  <p><strong>Product Type:</strong> {drug.openfda.product_type[0]}</p>
                )}
              </div>

              {drug.products && drug.products.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Product Details</h4>
                  <p><strong>Dosage Form:</strong> {drug.products[0].dosage_form || 'N/A'}</p>
                  <p><strong>Route:</strong> {drug.products[0].route || 'N/A'}</p>
                  <p><strong>Marketing Status:</strong> {drug.products[0].marketing_status || 'N/A'}</p>
                  {drug.products[0].active_ingredients && (
                    <div>
                      <strong>Active Ingredients:</strong>
                      <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                        {drug.products[0].active_ingredients.map((ing, i) => (
                          <li key={i}>{ing.name} - {ing.strength}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {drug.openfda?.substance_name && drug.openfda.substance_name.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Substances</h4>
                  <p>{drug.openfda.substance_name.join(', ')}</p>
                </div>
              )}

              {drug.openfda?.pharm_class_epc && drug.openfda.pharm_class_epc.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Pharmacologic Class</h4>
                  <p>{drug.openfda.pharm_class_epc.join(', ')}</p>
                </div>
              )}

              {drug.submissions && drug.submissions.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Recent Submission</h4>
                  <p><strong>Type:</strong> {drug.submissions[0].submission_type || 'N/A'}</p>
                  <p><strong>Status:</strong> {drug.submissions[0].submission_status || 'N/A'}</p>
                  {drug.submissions[0].submission_status_date && (
                    <p><strong>Date:</strong> {drug.submissions[0].submission_status_date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}</p>
                  )}
                </div>
              )}

              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #ecf0f1', textAlign: 'center' }}>
                <span className="view-details-link">View Full Details →</span>
              </div>
            </Link>
          );
          })}
        </div>
      );
    }

    if (searchType === 'adverse' && results.results) {
      if (results.results.length === 0) {
        return <p className="no-results">No adverse events found for "{searchQuery}"</p>;
      }
      
      return (
        <div className="results-grid">
          {results.results.map((event, index) => (
            <div key={index} className="result-card hoverable-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>Adverse Event Report #{index + 1}</h3>
                <span className="badge badge-warning" style={{ fontSize: '0.75rem', flexShrink: 0, marginLeft: '0.5rem' }}>FAERS</span>
              </div>
              
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Report Details</h4>
                <p><strong>Serious:</strong> <span style={{ color: event.serious === '1' ? '#e74c3c' : '#27ae60' }}>{event.serious === '1' ? 'Yes ⚠️' : 'No'}</span></p>
                <p><strong>Receive Date:</strong> {event.receivedate ? event.receivedate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3') : 'N/A'}</p>
                <p><strong>Report Source:</strong> {event.primarysource?.qualification || 'N/A'}</p>
                {event.reporttype && <p><strong>Report Type:</strong> {event.reporttype}</p>}
              </div>

              {event.patient?.reaction && event.patient.reaction.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Adverse Reactions</h4>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                    {event.patient.reaction.map((r, i) => (
                      <li key={i}>
                        <strong>{r.reactionmeddrapt}</strong>
                        {r.reactionoutcome && ` - Outcome: ${r.reactionoutcome}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {event.patient?.drug && event.patient.drug.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Drugs Involved</h4>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                    {event.patient.drug.map((d, i) => (
                      <li key={i}>
                        <strong>{d.medicinalproduct}</strong>
                        {d.drugcharacterization && ` - ${d.drugcharacterization === '1' ? 'Suspect' : 'Concomitant'}`}
                        {d.drugdosagetext && <div style={{ fontSize: '0.9em', color: '#7f8c8d' }}>Dosage: {d.drugdosagetext}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {event.patient?.patientonsetage && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Patient Information</h4>
                  <p><strong>Age:</strong> {event.patient.patientonsetage} {event.patient.patientonsetageunit === '801' ? 'years' : 'months'}</p>
                  {event.patient.patientsex && (
                    <p><strong>Sex:</strong> {event.patient.patientsex === '1' ? 'Male' : event.patient.patientsex === '2' ? 'Female' : 'Unknown'}</p>
                  )}
                </div>
              )}

              {event.serious === '1' && event.seriousnesscongenitalanomali === '1' && (
                <div style={{ marginTop: '1rem', padding: '0.5rem', backgroundColor: '#ffe6e6', borderRadius: '4px' }}>
                  <strong style={{ color: '#c0392b' }}>⚠️ Serious Event</strong>
                  {event.seriousnessdeath === '1' && <p>• Resulted in death</p>}
                  {event.seriousnesslifethreatening === '1' && <p>• Life threatening</p>}
                  {event.seriousnesshospitalization === '1' && <p>• Required hospitalization</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (searchType === 'labels' && results.results) {
      if (results.results.length === 0) {
        return <p className="no-results">No labels found for "{searchQuery}"</p>;
      }
      
      return (
        <div className="results-grid">
          {results.results.map((label, index) => (
            <div key={index} className="result-card hoverable-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>{label.openfda?.brand_name?.[0] || 'Drug Label'}</h3>
                <span className="badge badge-success" style={{ fontSize: '0.75rem', flexShrink: 0, marginLeft: '0.5rem' }}>Label</span>
              </div>
              
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Drug Information</h4>
                <p><strong>Generic Name:</strong> {label.openfda?.generic_name?.[0] || 'N/A'}</p>
                <p><strong>Manufacturer:</strong> {label.openfda?.manufacturer_name?.[0] || 'N/A'}</p>
                {label.openfda?.product_type && (
                  <p><strong>Product Type:</strong> {label.openfda.product_type[0]}</p>
                )}
                {label.openfda?.route && (
                  <p><strong>Route:</strong> {label.openfda.route.join(', ')}</p>
                )}
              </div>

              {label.purpose && label.purpose.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Purpose</h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{label.purpose[0].substring(0, 300)}{label.purpose[0].length > 300 ? '...' : ''}</p>
                </div>
              )}

              {label.indications_and_usage && label.indications_and_usage.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Indications and Usage</h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{label.indications_and_usage[0].substring(0, 400)}{label.indications_and_usage[0].length > 400 ? '...' : ''}</p>
                </div>
              )}

              {label.dosage_and_administration && label.dosage_and_administration.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Dosage and Administration</h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{label.dosage_and_administration[0].substring(0, 300)}{label.dosage_and_administration[0].length > 300 ? '...' : ''}</p>
                </div>
              )}

              {label.warnings && label.warnings.length > 0 && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fff3cd', borderRadius: '4px', borderLeft: '4px solid #ffc107' }}>
                  <h4 style={{ color: '#856404', marginBottom: '0.5rem', marginTop: 0 }}>⚠️ Warnings</h4>
                  <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{label.warnings[0].substring(0, 400)}{label.warnings[0].length > 400 ? '...' : ''}</p>
                </div>
              )}

              {label.adverse_reactions && label.adverse_reactions.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Adverse Reactions</h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{label.adverse_reactions[0].substring(0, 300)}{label.adverse_reactions[0].length > 300 ? '...' : ''}</p>
                </div>
              )}

              {label.drug_interactions && label.drug_interactions.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Drug Interactions</h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{label.drug_interactions[0].substring(0, 300)}{label.drug_interactions[0].length > 300 ? '...' : ''}</p>
                </div>
              )}

              {label.active_ingredient && label.active_ingredient.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Active Ingredients</h4>
                  <p>{label.active_ingredient.join(', ')}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    return <p className="no-results">No results found</p>;
  };

  return (
    <div className="page-container">
      <SEO
        title="FDA Drug Search - Search FDA-Approved Drugs & Medications"
        description="Search FDA-approved drugs from Drugs@FDA database. Access drug information, approval history, safety data, adverse events, and official drug labels."
        keywords="FDA drugs, drug search, medication search, FDA approved drugs, drug information, drug labels, adverse events, drug safety, pharmaceutical database"
        canonical="/drugs"
        structuredData={seoStructuredData}
      />
      
      <h1 className="page-title">FDA Drug Search</h1>
      <p className="page-description">
        Search FDA-approved drugs, view adverse events, and access drug labels from the Drugs@FDA database.
      </p>

      <div className="search-section">
        <form onSubmit={handleSearch}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ marginRight: '1rem' }}>
              <input
                type="radio"
                value="search"
                checked={searchType === 'search'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              {' '}Drug Search
            </label>
            <label style={{ marginRight: '1rem' }}>
              <input
                type="radio"
                value="adverse"
                checked={searchType === 'adverse'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              {' '}Adverse Events
            </label>
            <label>
              <input
                type="radio"
                value="labels"
                checked={searchType === 'labels'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              {' '}Drug Labels
            </label>
          </div>

          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Enter drug name (e.g., aspirin, ibuprofen)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error">Error: {error}</div>}
      
      {loading && <div className="loading">Loading...</div>}

      {!loading && results && (
        <div className="results-section">
          <div className="results-header">
            <h2>Results ({results.results?.length || 0})</h2>
            {totalPages > 1 && (
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
            )}
          </div>
          {renderResults()}

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="pagination-btn"
              >
                ← Previous
              </button>

              <div className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      <RecentSearchesSidebar database="drugs" />
    </div>
  );
}

export default DrugSearch;
